import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../hooks/useAuthCompat";
import { useApi } from "../hooks/useApi";
import { toast } from "sonner";
import { Radio, Zap, Film, Archive, Check, ShieldCheck } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d18f6363-3058-4bfd-b277-f958917ab691/artifacts/i3gab7l7_EB%20Gold%20logo.png";
const HERO_URL = "https://customer-assets.emergentagent.com/job_d18f6363-3058-4bfd-b277-f958917ab691/artifacts/gocf68d7_60D7CC8F-C7E7-4D49-A962-78B54C3E34CC.jpeg";

const BENEFITS = [
  { icon: Radio, title: "Audio Briefings", body: "Weekly briefings from Darius — short, declarative, built for the commute." },
  { icon: Zap, title: "Weekly Signal", body: "Announcements and field updates the moment they're issued." },
  { icon: Film, title: "Gold Video Library", body: "The full catalog of Gold video briefings, growing every week." },
  { icon: Archive, title: "Member Archive", body: "Every past Briefing and Signal, searchable, always available." },
];

const TIER_META = [
  { key: "gold", name: "GOLD", monthlyKey: "gold_monthly", annualKey: "gold_annual", monthlyPrice: 19, annualPrice: 182.4, monthlyEq: 15.2,
    features: ["Audio Briefings", "Weekly Signal announcements", "Gold Video Library", "Full member archive"] },
  { key: "gold_pro", name: "GOLD PRO", monthlyKey: "gold_pro_monthly", annualKey: "gold_pro_annual", monthlyPrice: 100, annualPrice: 960, monthlyEq: 80,
    features: ["Everything in GOLD", "Efficiency Frameworks — full library", "Companion Assets"] },
];

const FAQ = [
  { q: "Can I cancel anytime?", a: "Yes. Cancel from Account → Billing whenever you like — your access continues through the end of the period you already paid for." },
  { q: "What's the difference between GOLD and GOLD PRO?", a: "GOLD gives you the signal — Audio Briefings, Weekly Signal, the Video Library, and the full archive. GOLD PRO adds the tools — Efficiency Frameworks and Companion Assets — on top of everything in GOLD." },
  { q: "Do I need an account before I pay?", a: "No. Click Join GOLD, pay first, and we'll email you a one-click link to activate your account — no password required." },
  { q: "Is my payment secure?", a: "Yes — all payments are processed directly by Stripe. We never see or store your card details." },
  { q: "Can I switch between GOLD and GOLD PRO later?", a: "Yes, in one click from Account → Billing — you're switched in place, never billed twice." },
  { q: "What happens if my payment fails?", a: "You get a 72-hour grace period with full access while you update your payment method, before access pauses." },
  { q: "How do I get a receipt?", a: "Every purchase emails a receipt automatically, and you can re-view or resend it anytime from Account → Billing." },
];

export default function Gold() {
  const api = useApi();
  const { isSignedIn } = useUser();
  const [params] = useSearchParams();
  const canceled = params.get("canceled") === "1";
  const [cycle, setCycle] = useState("monthly");
  const [checkingOut, setCheckingOut] = useState(null);
  const { data: membership } = useQuery({ queryKey: ["gold-membership"], queryFn: async () => (await api.get("/gold/membership")).data, enabled: !!isSignedIn });

  const activeTier = membership?.product_type;

  const subscribe = async (tier) => {
    const meta = TIER_META.find((t) => t.key === tier);
    const offerKey = cycle === "monthly" ? meta.monthlyKey : meta.annualKey;
    setCheckingOut(offerKey);
    try {
      const path = isSignedIn ? "/billing/checkout" : "/gold/enroll/checkout";
      const res = await api.post(path, { offer_key: offerKey, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setCheckingOut(null);
    }
  };

  return (
    <div className="theme-gold min-h-screen" data-testid="gold-page">
      {canceled && (
        <div className="bg-white/5 border-b border-white/10 py-3 text-center" data-testid="gold-canceled-banner">
          <p className="text-sm text-foreground/70 font-mono">Checkout was not completed. You can join anytime below.</p>
        </div>
      )}

      {/* Hero */}
      <section className="relative min-h-[80vh] sm:min-h-[88vh] flex items-end overflow-hidden" data-testid="gold-hero">
        <img src={HERO_URL} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 18%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.55) 42%, rgba(0,0,0,0.92) 80%, #000 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.28) 38%, transparent 62%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20 w-full">
          <img src={LOGO_URL} alt="Endure Beyond Gold" className="w-10 h-10 mb-6" />
          <p data-testid="gold-eyebrow" className="text-[#C9A227] font-meta text-[10px] uppercase tracking-[0.2em] mb-4">The Gold Vault</p>
          <h1 className="font-display font-semibold tracking-tight leading-[1.05] text-[clamp(1.75rem,6.5vw,4rem)] mb-5" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.9)" }}>
            BE BOLD. GO <span className="gold-shimmer">GOLD.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-xl mb-8">
            A private intelligence vault building stronger humans for Earth and Beyond.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <button data-testid="gold-hero-join-gold" onClick={() => subscribe("gold")} disabled={!!checkingOut}
              className="gold-metallic-btn h-12 px-7 font-semibold text-sm rounded-lg active:scale-[0.98] disabled:opacity-70 transition-transform"
              style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.3)" }}>
              {checkingOut === "gold_monthly" ? "Redirecting…" : "→ Join GOLD — $19/month"}
            </button>
            <button data-testid="gold-hero-join-gold-pro" onClick={() => subscribe("gold_pro")} disabled={!!checkingOut}
              className="h-12 px-7 rounded-lg text-sm font-semibold border disabled:opacity-70 transition-colors"
              style={{ borderColor: "rgba(201,162,39,0.5)", color: "#F0D060" }}>
              {checkingOut === "gold_pro_monthly" ? "Redirecting…" : "Join GOLD PRO — $100/month"}
            </button>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-meta text-white/55">
            <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#C9A227]" /> Secure payment via Stripe</span>
            <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#C9A227]" /> Cancel anytime</span>
            <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#C9A227]" /> All devices</span>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Inside the Vault */}
        <section id="gold-vault" className="scroll-mt-20 pb-16 sm:pb-20 pt-16 sm:pt-20" data-testid="gold-benefits-section">
          <p className="text-[#C9A227] font-meta text-[10px] uppercase tracking-[0.2em] mb-3">What You Get</p>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-10">Inside the Gold Vault</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {BENEFITS.map((b) => (
              <div key={b.title} className="bg-[#0a0a0a] border border-white/[0.07] rounded-xl p-4 sm:p-5 hover:border-white/[0.12] transition-colors" data-testid={`gold-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <b.icon className="w-[18px] h-[18px] text-[#C9A227] mb-3" />
                <p className="font-display text-sm font-semibold leading-snug mb-1.5">{b.title}</p>
                <p className="text-white/50 text-xs leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Memberships */}
        <section id="memberships" className="scroll-mt-20 pb-16 sm:pb-20 max-w-3xl mx-auto" data-testid="gold-memberships-section">
          <p className="text-[#C9A227] font-meta text-[10px] uppercase tracking-[0.2em] mb-3 text-center">Memberships</p>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-3 text-center">GOLD or GOLD PRO.</h2>
          <p className="text-white/40 text-sm font-meta text-center mb-8">Vault gives you the signal. Gold Pro gives you the tools.</p>

          <div className="flex justify-center items-center gap-3 mb-10">
            <div className="rounded-full p-1 bg-white/[0.05] border border-white/[0.08] inline-flex">
              <button data-testid="gold-cycle-monthly" onClick={() => setCycle("monthly")}
                className="px-4 py-1.5 text-xs font-meta uppercase rounded-full transition-colors"
                style={cycle === "monthly" ? { background: "rgba(201,162,39,0.15)", color: "#C9A227", border: "1px solid rgba(201,162,39,0.35)" } : { color: "rgba(255,255,255,0.35)" }}>
                Monthly
              </button>
              <button data-testid="gold-cycle-annual" onClick={() => setCycle("annual")}
                className="px-4 py-1.5 text-xs font-meta uppercase rounded-full transition-colors"
                style={cycle === "annual" ? { background: "rgba(201,162,39,0.15)", color: "#C9A227", border: "1px solid rgba(201,162,39,0.35)" } : { color: "rgba(255,255,255,0.35)" }}>
                Annual
              </button>
            </div>
            {cycle === "annual" && (
              <span data-testid="gold-annual-savings-badge" className="text-[9px] font-bold rounded-full px-2 py-1" style={{ background: "#C9A227", color: "#0a0a0a" }}>
                SAVE 20%
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {TIER_META.map((t) => {
              const isCurrentTier = activeTier === t.key;
              const isIncludedViaHigherTier = t.key === "gold" && activeTier === "gold_pro";
              const price = cycle === "monthly" ? t.monthlyPrice : t.annualPrice;
              const offerKey = cycle === "monthly" ? t.monthlyKey : t.annualKey;
              const isPro = t.key === "gold_pro";
              return (
                <div key={t.key} data-testid={`gold-tier-${t.key}`}
                  className="bg-[#0a0a0a] rounded-2xl p-6 flex flex-col border"
                  style={{ borderColor: isPro ? "rgba(201,162,39,0.35)" : "rgba(255,255,255,0.08)" }}>
                  <span className="inline-block w-fit text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded mb-4"
                    style={{ background: "rgba(201,162,39,0.10)", border: "1px solid rgba(201,162,39,0.25)", color: "#C9A227" }}>
                    {t.name}
                  </span>
                  <p className="font-display text-3xl font-bold mb-1">
                    ${price % 1 === 0 ? price : price.toFixed(2)}
                    <span className="text-white/50 text-sm font-body">/{cycle === "monthly" ? "mo" : "yr"}</span>
                  </p>
                  {cycle === "annual" ? <p className="text-white/30 text-xs font-mono mb-5">${t.monthlyEq.toFixed(2)}/mo equivalent · Save 20%</p> : <div className="mb-5" />}
                  <ul className="space-y-2 mb-6">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                        <Check className="w-3 h-3 text-[#C9A227] mt-1 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    {isCurrentTier ? (
                      <span data-testid={`gold-current-plan-${t.key}`} className="inline-block text-xs uppercase tracking-wide text-[#C9A227] border border-[#C9A227] px-3 py-1.5 rounded-lg">Current Plan</span>
                    ) : isIncludedViaHigherTier ? (
                      <span className="inline-block text-xs uppercase tracking-wide text-white/50 border border-white/20 px-3 py-1.5 rounded-lg">Included with Gold Pro</span>
                    ) : (
                      <>
                        <button data-testid={`gold-subscribe-${t.key}`} onClick={() => subscribe(t.key)} disabled={checkingOut === offerKey}
                          className={isPro ? "h-11 w-full rounded-lg text-sm font-semibold disabled:opacity-50 transition-opacity" : "gold-metallic-btn h-11 w-full rounded-lg text-sm font-semibold disabled:opacity-50 transition-opacity"}
                          style={isPro ? { background: "#C9A227", color: "#0a0a0a" } : undefined}>
                          {checkingOut === offerKey ? "Redirecting…" : activeTier ? `Upgrade to ${t.name}` : `Join ${t.name} — $${price % 1 === 0 ? price : price.toFixed(2)}/${cycle === "monthly" ? "mo" : "yr"}`}
                        </button>
                        <p data-testid={`gold-secure-checkout-badge-${t.key}`} className="text-xs text-white/40 flex items-center gap-1.5 mt-3">
                          <ShieldCheck className="w-3.5 h-3.5 text-[#C9A227]" /> Secure payment via Stripe · Cancel anytime
                        </p>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <Link to="/mods" data-testid="gold-mods-promo-card" className="block bg-[#0a0807] border border-amber-500/25 rounded-2xl p-6 mb-16 sm:mb-20 hover:border-amber-500/50 transition-colors duration-200">
          <p className="text-xs uppercase tracking-wide text-amber-400 mb-1 font-semibold">MODS — Executive Tier</p>
          <p className="text-sm text-white/60">Everything in GOLD and GOLD PRO + the Mars Operator Development School — $10,000/mo. View details →</p>
        </Link>

        {/* FAQ */}
        <section className="pb-16 sm:pb-20 max-w-2xl mx-auto" data-testid="gold-faq-section">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-8 text-center">Field Questions</h2>
          <Accordion type="single" collapsible className="divide-y divide-white/[0.07]">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/[0.07]" data-testid={`gold-faq-item-${i}`}>
                <AccordionTrigger className="text-white/85 text-sm font-medium hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-white/55 text-sm">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Closing CTA */}
        <section className="pb-20 sm:pb-24 text-center" data-testid="gold-closing-cta">
          <p className="text-white/25 text-xs font-mono uppercase tracking-[0.2em] mb-6">Choose Your Next Step</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button data-testid="gold-closing-join-gold" onClick={() => subscribe("gold")} disabled={!!checkingOut}
              className="gold-metallic-btn px-8 py-3.5 rounded-full text-sm font-semibold disabled:opacity-70">
              Join GOLD — $19/month
            </button>
            <button data-testid="gold-closing-join-gold-pro" onClick={() => subscribe("gold_pro")} disabled={!!checkingOut}
              className="px-8 py-3.5 rounded-full text-sm font-semibold border" style={{ borderColor: "rgba(201,162,39,0.5)", color: "#F0D060" }}>
              Join GOLD PRO — $100/month
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
